import Tag from "components/design/Tag";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { COMPANY_GROUP_TAG } from "constants/tagCategory";
import { CHURNED } from "constants/TagType";
import { Form, Formik, FormikValues } from "formik";
import { opsBulkActionSchema } from "formValidations/opsBulkActionSchema";
import { useToast } from "hooks/useToast";
import { MultiValue, SingleValue } from "react-select";
import { useGetTagsForCategoryQuery } from "store/apis/billing";
import { useUpdateBulkCompanyGroupMutation } from "store/apis/salesCrm";
import { BackendError } from "types/utils/error";
import { OpsBulkActionConfig, OpsBulkActionType } from "./OpsBulkAction";

type BulkTagAssignProps = {
  isOpen: boolean;
  close: () => void;
  selectedRows: string[];
  actionType: OpsBulkActionType;
  resetSelection: () => void;
  actionDetails: OpsBulkActionConfig;
};

export const BulkTagAssign = ({
  isOpen,
  close,
  selectedRows,
  actionType,
  resetSelection,
  actionDetails,
}: BulkTagAssignProps) => {
  const [updateBulkCompanyGroup, { isLoading: isUpdating }] =
    useUpdateBulkCompanyGroupMutation();
  const { alertToast, successToast } = useToast();
  const { data: tagsData = [], isLoading } = useGetTagsForCategoryQuery({
    tagType: COMPANY_GROUP_TAG,
  });
  const { toastTitle, toastMessage, title, label } = actionDetails;

  // CHURNED can't be bulk-assigned -- it requires per-group churn details captured
  // through ChurnDetailsModal (see the comment on CHURNED in constants/TagType.ts).
  const companyTagsData = tagsData.filter(
    ({ title: tagTitle }) => tagTitle !== CHURNED
  );

  const handleUpdate = async (values: FormikValues) => {
    try {
      await updateBulkCompanyGroup({
        payload: {
          group_ids: selectedRows,
          update_key: actionType,
          update_value: values.value.join(","),
        },
      }).unwrap();
      successToast({
        title: toastTitle,
        message: `${toastMessage} ${selectedRows.length} clients`,
      });
      close();
      resetSelection();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content useCustomOverlay>
        <Modal.Header>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{ value: [] as string[] }}
          onSubmit={handleUpdate}
          enableReinitialize
          validationSchema={opsBulkActionSchema}
        >
          {({ handleSubmit, setFieldValue, values }) => {
            const onTagsChange = (
              selectedTags: MultiValue<OptionData> | SingleValue<OptionData>
            ) => {
              setFieldValue(
                "value",
                (selectedTags as MultiValue<OptionData>).map((tag) => tag.value)
              );
            };

            const selectedTags = companyTagsData.filter((tag) =>
              values.value.includes(tag.uuid)
            );
            return (
              <Form onSubmit={handleSubmit} className="t-m-0">
                <Modal.Body>
                  {isLoading ? (
                    <Loader size="small" />
                  ) : (
                    <Combobox
                      menuPortalTarget={document.body}
                      isMulti
                      styles={{
                        menu: () => ({ width: "400px" }),
                      }}
                      name="value"
                      label={label}
                      options={companyTagsData?.map(
                        (t: { title: string; uuid: string }) => ({
                          label: t.title,
                          value: t.uuid,
                        })
                      )}
                      value={selectedTags?.map(
                        (tag: {
                          uuid: string;
                          title: string;
                          bg_color_hex: string;
                          color_hex: string;
                        }) => ({
                          value: tag.uuid,
                          label: (
                            <Tag
                              size="small"
                              key={tag.uuid}
                              title={tag.title}
                              bg_color_hex={tag.bg_color_hex}
                              color_hex={tag.color_hex}
                            />
                          ),
                        })
                      )}
                      onChange={(
                        selectedTags:
                          | SingleValue<OptionData>
                          | MultiValue<OptionData>
                      ) => {
                        if (selectedTags && selectedTags instanceof Array) {
                          onTagsChange?.(
                            (selectedTags as {
                              label: string;
                              value: string;
                            }[]) || []
                          );
                        }
                      }}
                      isLoading={isLoading}
                      backspaceRemovesValue={false}
                    />
                  )}
                </Modal.Body>

                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button type="button">Cancel</Button>
                  </Modal.RawClose>
                  <Button
                    customType="primary"
                    type="submit"
                    isLoading={isUpdating}
                  >
                    Assign all ({selectedRows.length})
                  </Button>
                </Modal.FooterButtonGroup>
              </Form>
            );
          }}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
